import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Button,
  Box, 
  Typography,
  Alert,
  Snackbar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { FaEdit } from 'react-icons/fa';

// Components
import CustomButton from "../../components/common/CustomButton/CustomButton.tsx";
import { Column } from "../../components/common/CustomDataTable/CustomDataTable.tsx";
import { CustomTabPanel, SubHeaderTitle } from "../../components/common/index";
import { CommonData } from "../PageImports.ts";
import ErrorPage from "../Error/ErrorPage.tsx";
import Loader from "../../utils/Loader.tsx";

// Utilities
import { axiosFunction } from '../../components/commonFunctions/axiosFunction.ts';
import { v2ReactURL } from '../../utils/constant.ts';
import { capitalizeFirstChar, CheckedValue, debounce, ErrorState, Pagination } from '../Contact/MainDataTable.tsx';

const {
  Wrapper,
  WrapperSubHeader,
  WrapperContent,
  WrapperCard,
  Row,
  CustomDataTable,
} = CommonData;

// const { toastMessage } = CommonFunctions;

interface DeliveryChargeProps {
  navUrl: string;
}

interface DeliveryChargeData {
  nativeDeliveryChargeId: number;
  nativeDeliveryChargeName: string;
  nativeDeliveryChargeValue: number;
  fromRange: number;
  toRange: number;
  active: number;
  createdBy: string;
  startDate: string;
  endDate: string;
  endTime?: string;
  startTime?: string;
}

interface DeliveryChargeFormData {
  nativeDeliveryChargeId: number;
  nativeDeliveryChargeName: string;
  nativeDeliveryChargeValue: number;
  fromRange: number;
  toRange: number;
  active: number;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
}

interface UserPermissions {
  canView: boolean;
  canInsert: boolean;
  canEdit: boolean;
  canDelete: boolean;
  loading: boolean;
  error: string | null;
}

interface ValidationErrors {
  nativeDeliveryChargeName?: string;
  fromRange?: string;
  toRange?: string;
  nativeDeliveryChargeValue?: string;
}

// Create a reusable NumberInput component to handle zero values properly
const NumberInput = ({ 
  value, 
  onChange, 
  placeholder = "0",
  error = false,
  helperText = "",
  required = false,
  ...props 
}: any) => {
  const [inputValue, setInputValue] = useState<string>(value === 0 ? '' : value.toString());

  // Update local state when external value changes
  React.useEffect(() => {
    setInputValue(value === 0 ? '' : value.toString());
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    
    // Convert to number: empty string becomes 0, otherwise parse as float
    if (val === '') {
      onChange(0);
    } else {
      const numValue = parseFloat(val);
      onChange(isNaN(numValue) ? 0 : numValue);
    }
  };

  return (
    <TextField
      type="number"
      fullWidth
      size="small"
      value={inputValue}
      onChange={handleChange}
      placeholder={placeholder}
      required={required}
      error={error}
      helperText={helperText}
      {...props}
    />
  );
};

const DeliveryCharge: React.FC<DeliveryChargeProps> = ({ navUrl }) => {
  const tabView = navUrl;
  
  // Permission state - REPLACED usePermission hook
  const [userPermissions, setUserPermissions] = useState<UserPermissions>({
    canView: false,
    canInsert: false,
    canEdit: false,
    canDelete: false,
    loading: true,
    error: null
  });

  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [rows, setRows] = useState<DeliveryChargeData[]>([]);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [pagination, setPagination] = useState<Pagination>({
    page: 0,
    pages: 0,
    perpage: 0,
    total: 0,
  });
  const [pageOption, setPageOption] = useState<string>('15');
  const [searchValue, setSearchValue] = useState<string>('');
  const [error, setError] = useState<ErrorState | null>(null);
  const [checkedValue, setCheckedValue] = useState<CheckedValue>({});
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [searchInputValue, setSearchInputValue] = useState<string>('');

  // Form state
  const [formData, setFormData] = useState<DeliveryChargeFormData>({
    nativeDeliveryChargeId: 0,
    nativeDeliveryChargeName: '',
    nativeDeliveryChargeValue: 0,
    fromRange: 0,
    toRange: 0,
    active: 1,
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
  });

  const [showValidation, setShowValidation] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [validating, setValidating] = useState<boolean>(false);

  // Snackbar state for toast messages
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
    vertical: 'bottom' as 'top' | 'bottom',
    horizontal: 'right' as 'left' | 'center' | 'right'
  });

  // Show snackbar function
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
      vertical: 'bottom',
      horizontal: 'right'
    });
  };

  const handleSnackbarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Override toastMessage function to use our custom snackbar
  const customToastMessage = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    showSnackbar(message, type);
  };

  // Validation API calls
  const checkDeliveryChargeName = async (name: string): Promise<boolean> => {
    if (!name.trim()) return true; // Empty name will be caught by required validation
    
    try {
      const payload = {
        deliveryChargeName: name.trim(),
        deliveryChargeId: formData.nativeDeliveryChargeId || 0 // Include ID for edit scenario
      };

      const res = await axiosFunction(
        `${v2ReactURL}/native/deliverycharge/checkDeliveryChargeName`,
        "POST",
        payload
      );

      // API should return true if name is available, false if already exists
      return res.data?.response === true;
    } catch (error: any) {
      console.error("Error checking delivery charge name:", error);
      // If API fails, assume name is valid to avoid blocking user
      return true;
    }
  };

  const checkFromRange = async (fromRange: number, toRange: number): Promise<boolean> => {
    if (fromRange === 0 && toRange === 0) return true;
    
    try {
      const payload = {
        fromRange: fromRange,
        toRange: toRange,
        nativeDeliveryChargeId: formData.nativeDeliveryChargeId || 0 // Include ID for edit scenario
      };

      const res = await axiosFunction(
        `${v2ReactURL}/native/deliveryCharge/checkFromRange`,
        "POST",
        payload
      );

      // API should return true if range is available, false if overlaps
      return res.data?.response === true;
    } catch (error: any) {
      console.error("Error checking from range:", error);
      // If API fails, assume range is valid to avoid blocking user
      return true;
    }
  };

  const checkToRange = async (fromRange: number, toRange: number): Promise<boolean> => {
    if (fromRange === 0 && toRange === 0) return true;
    
    try {
      const payload = {
        fromRange: fromRange,
        toRange: toRange,
        nativeDeliveryChargeId: formData.nativeDeliveryChargeId || 0 // Include ID for edit scenario
      };

      const res = await axiosFunction(
        `${v2ReactURL}/native/deliveryCharge/checkToRange`,
        "POST",
        payload
      );

      // API should return true if range is available, false if overlaps
      return res.data?.response === true;
    } catch (error: any) {
      console.error("Error checking to range:", error);
      // If API fails, assume range is valid to avoid blocking user
      return true;
    }
  };

  // Debounced validation functions
  const validateDeliveryChargeName = useCallback(
    debounce(async (name: string) => {
      if (!name.trim()) {
        setValidationErrors(prev => ({ ...prev, nativeDeliveryChargeName: undefined }));
        return;
      }

      setValidating(true);
      const isValid = await checkDeliveryChargeName(name);
      setValidating(false);

      if (!isValid) {
        setValidationErrors(prev => ({ 
          ...prev, 
          nativeDeliveryChargeName: "Delivery charge name already exists. Please choose a different name." 
        }));
      } else {
        setValidationErrors(prev => ({ ...prev, nativeDeliveryChargeName: undefined }));
      }
    }, 500),
    [formData.nativeDeliveryChargeId]
  );

  const validateRanges = useCallback(
    debounce(async (fromRange: number, toRange: number) => {
      if (fromRange === 0 && toRange === 0) {
        setValidationErrors(prev => ({ 
          ...prev, 
          fromRange: undefined,
          toRange: undefined 
        }));
        return;
      }

      if (fromRange >= toRange) {
        // This will be handled by the basic validation
        return;
      }

      setValidating(true);
      
      const [fromRangeValid, toRangeValid] = await Promise.all([
        checkFromRange(fromRange, toRange),
        checkToRange(fromRange, toRange)
      ]);

      setValidating(false);

      const errors: ValidationErrors = {};
      
      if (!fromRangeValid) {
        errors.fromRange = "This from range overlaps with an existing range. Please choose a different range.";
      }
      
      if (!toRangeValid) {
        errors.toRange = "This to range overlaps with an existing range. Please choose a different range.";
      }

      setValidationErrors(prev => ({ 
        ...prev, 
        fromRange: errors.fromRange,
        toRange: errors.toRange
      }));
    }, 500),
    [formData.nativeDeliveryChargeId]
  );

  // Fetch user permissions - DIRECT API CALL
  const fetchUserPermissions = async (): Promise<boolean> => {
    try {
      setUserPermissions(prev => ({ ...prev, loading: true, error: null }));
      
      const response = await axiosFunction(
        `${v2ReactURL}/native/getuserrolepermission`,
        'GET',
        false
      );

      console.log("Permission API response:", response.data.response.reportListResponse);

      if (response.data?.status && response.data.response?.reportListResponse) {
        const reportList = response.data.response.reportListResponse;
        
        // Check if reportList is an array and find delivery permissions
        if (Array.isArray(reportList)) {
          // Find the delivery type permissions by mapping through the array
          const deliveryPermissions = reportList.find((item: any) => 
            item.type  === 'deliveryCharges'
          );
          
          console.log("Found delivery permissions:", deliveryPermissions);
          
          if (deliveryPermissions && deliveryPermissions.permissionids) {
            let permissionArray: string[] = [];
            
            if (typeof deliveryPermissions.permissionids === 'string') {
              // Split comma-separated string into array
              permissionArray = deliveryPermissions.permissionids.split(',').map((id: string) => id.trim());
            } else if (Array.isArray(deliveryPermissions.permissionids)) {
              // If it's already an array, use it directly
              permissionArray = deliveryPermissions.permissionids.map((id: any) => id.toString());
            }
            
            console.log("Delivery permissions array:", permissionArray);
            
            const canView = permissionArray.includes('2');
            
            setUserPermissions({
              loading: false,
              canView: canView,
              canInsert: permissionArray.includes('1'),
              canEdit: permissionArray.includes('3'),
              canDelete: permissionArray.includes('4'),
              error: null
            });
            
            return canView;
          } else {
            // No delivery permissions found in the array
            console.warn('No delivery permissions found in reportListResponse array');
            setUserPermissions({
              loading: false,
              canView: false,
              canInsert: false,
              canEdit: false,
              canDelete: false,
              error: 'No delivery permissions found in the response'
            });
            return false;
          }
        } else {
          // reportListResponse is not an array
          console.error('reportListResponse is not an array:', reportList);
          setUserPermissions({
            loading: false,
            canView: false,
            canInsert: false,
            canEdit: false,
            canDelete: false,
            error: 'Invalid response format: reportListResponse is not an array'
          });
          return false;
        }
      } else {
        console.warn('Invalid API response structure');
        setUserPermissions({
          loading: false,
          canView: false,
          canInsert: false,
          canEdit: false,
          canDelete: false,
          error: response.data?.message || 'Failed to load permissions - invalid response structure'
        });
        return false;
      }
    } catch (error: any) {
      console.error('Error fetching permissions:', error);
      setUserPermissions({
        loading: false,
        canView: false,
        canInsert: false,
        canEdit: false,
        canDelete: false,
        error: 'Error fetching permissions from server'
      });
      return false;
    }
  };

  const fetchData = async (
    searchValue: string,
    startIndex: number,
    pageLength: number,
  ): Promise<void> => {
    setLoadingData(true);
    const payloadData = {
      searchValue: searchValue,
      length: pageLength,
      start: startIndex,
      draw: 1,
    };

    try {
      const res = await axiosFunction(
        `${v2ReactURL}/native/deliveryCharge/datatable`,
        "POST",
        payloadData
      );
      
      const responseData = res?.response?.data || res?.data || res;
      
      if (responseData && responseData.response) {
        const deliveryChargeData = responseData.response;
        
        if (deliveryChargeData.data && Array.isArray(deliveryChargeData.data)) {
          const deliveryChargeList = deliveryChargeData.data.map((col: DeliveryChargeData, index: number) => ({
            ...col,
            index: index + 1,
            checked: false,
          }));
          
          setPagination(deliveryChargeData.dataTableMetaDTO || {
            page: 0,
            pages: 1,
            perpage: pageLength,
            total: deliveryChargeData.data.length
          });
          setRows(deliveryChargeList);
        }
      } else if (responseData && Array.isArray(responseData.data)) {
        const deliveryChargeList = responseData.data.map((col: DeliveryChargeData, index: number) => ({
          ...col,
          index: index + 1,
          checked: false,
        }));
        
        setPagination(responseData.dataTableMetaDTO || {
          page: 0,
          pages: 1,
          perpage: pageLength,
          total: responseData.data.length
        });
        setRows(deliveryChargeList);
      } else {
        throw new Error("Invalid API response format");
      }
      
      setIsLoading(false);
      setLoadingData(false);
      
    } catch (error: any) {
      console.error("Error fetching delivery charge data:", error);
      handleApiError(error);
    }
  };

  const handleApiError = (error: any) => {
    if (error.code === "ERR_NETWORK" || error.message === "network error") {
      setError({
        code: "502",
        message: "Hang tight! Our checkout lanes are undergoing maintenance. We'll be back to serving you real soon!",
      });
    } else if (error.response?.status === 400) {
      setError({
        code: "400",
        message: "Oops, your request got lost in translation! Maybe try again with simpler instructions?",
      });
    } else if (error.response?.status === 404) {
      setError({
        code: "404",
        message: "Oopsie! Looks like this page went on vacation. Maybe try a different path?",
      });
    } else if (error.response?.status === 500) {
      setError({
        code: "500",
        message: "Uh-oh, our server's taking a coffee break. It'll be back to ringing up sales soon!",
      });
    } else {
      setError({
        code: "500",
        message: "An unexpected error occurred while fetching delivery charge data.",
      });
    }
    
    customToastMessage("Error fetching delivery charge details", 'error');
    setLoadingData(false);
    setIsLoading(false);
  };

  const handleSaveDeliveryCharge = async () => {
    // Set validation state to true to show all errors
    setShowValidation(true);

    // Basic validation
    if (!formData.nativeDeliveryChargeName.trim()) {
      customToastMessage("Delivery charge name is required", 'error');
      return;
    }

    // Delivery Charge Value is mandatory - must be greater than 0
    if (formData.nativeDeliveryChargeValue <= 0) {
      customToastMessage("Delivery charge value is required and must be greater than 0", 'error');
      return;
    }

    if (formData.fromRange < 0) {
      customToastMessage("From range cannot be negative", 'error');
      return;
    }

    if (formData.toRange < 0) {
      customToastMessage("To range cannot be negative", 'error');
      return;
    }

    if (formData.toRange <= formData.fromRange) {
      customToastMessage("To range must be greater than from range", 'error');
      return;
    }

    // Check for validation errors from API
    if (validationErrors.nativeDeliveryChargeName || validationErrors.fromRange || validationErrors.toRange) {
      customToastMessage("Please fix the validation errors before saving", 'error');
      return;
    }

    // If validating, wait for validation to complete
    if (validating) {
      customToastMessage("Please wait while we validate your data", 'info');
      return;
    }

    // Validate date logic
    const startDateTime = new Date(`${formData.startDate}T${formData.startTime || '00:00'}`);
    const endDateTime = new Date(`${formData.endDate}T${formData.endTime || '23:59'}`);
    if (endDateTime <= startDateTime) {
      customToastMessage("End date must be after start date", 'error');
      return;
    }

    // Final API validation before saving
    setSaving(true);
    try {
      // Perform final validation checks
      const [nameValid, fromRangeValid, toRangeValid] = await Promise.all([
        checkDeliveryChargeName(formData.nativeDeliveryChargeName),
        checkFromRange(formData.fromRange, formData.toRange),
        checkToRange(formData.fromRange, formData.toRange)
      ]);

      if (!nameValid) {
        customToastMessage("Delivery charge name already exists. Please choose a different name.", 'error');
        setSaving(false);
        return;
      }

      if (!fromRangeValid || !toRangeValid) {
        customToastMessage("The specified range overlaps with an existing range. Please choose a different range.", 'error');
        setSaving(false);
        return;
      }

      // If all validations pass, proceed with saving
      const payload = {
        nativeDeliveryChargeId: formData.nativeDeliveryChargeId,
        nativeDeliveryChargeName: formData.nativeDeliveryChargeName.trim(),
        nativeDeliveryChargeValue: formData.nativeDeliveryChargeValue,
        fromRange: formData.fromRange,
        toRange: formData.toRange,
      };

      const res = await axiosFunction(
        `${v2ReactURL}/native/saveDeliveryCharge`,
        "POST",
        payload
      );

      if (res.status === 200 || res.status === 201) {
        customToastMessage(`Delivery charge ${formData.nativeDeliveryChargeId ? 'updated' : 'created'} successfully!`, 'success');
        setOpenCreateModal(false);
        resetForm();
        handleFetchData();
      } else {
        customToastMessage(res.data?.message || `Failed to ${formData.nativeDeliveryChargeId ? 'update' : 'create'} delivery charge`, 'error');
      }
    } catch (error: any) {
      console.error("Error saving delivery charge:", error);
      customToastMessage(`Error ${formData.nativeDeliveryChargeId ? 'updating' : 'creating'} delivery charge`, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Delete delivery charge function
  const handleDeleteDeliveryCharge = async (deliveryChargeId: number) => {
    // if (!window.confirm('Are you sure you want to delete this delivery charge?')) {
    //   return;
    // }

    setDeleting(true);
    try {
      const res = await axiosFunction(
        `${v2ReactURL}/native/deliveryCharge/${deliveryChargeId}/delete`,
        "POST",
        {},
        false
      );

      if (res.status === 200 || res.status === 201) {
        customToastMessage('Delivery charge deleted successfully!', 'success');
        handleFetchData(); // Refresh the data
      } else {
        customToastMessage(res.data?.message || 'Failed to delete delivery charge', 'error');
      }
    } catch (error: any) {
      console.error("Error deleting delivery charge:", error);
      customToastMessage('Error deleting delivery charge', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleEditDeliveryCharge = (row: DeliveryChargeData) => {
    setFormData({
      nativeDeliveryChargeId: row.nativeDeliveryChargeId,
      nativeDeliveryChargeName: row.nativeDeliveryChargeName,
      nativeDeliveryChargeValue: row.nativeDeliveryChargeValue,
      fromRange: row.fromRange,
      toRange: row.toRange,
      active: row.active,
      startDate: row.startDate,
      endDate: row.endDate,
      startTime: row.startTime || '00:00',
      endTime: row.endTime || '23:59',
    });
    setShowValidation(false);
    setValidationErrors({});
    setOpenCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      nativeDeliveryChargeId: 0,
      nativeDeliveryChargeName: '',
      nativeDeliveryChargeValue: 0,
      fromRange: 0,
      toRange: 0,
      active: 1,
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
    });
    setShowValidation(false);
    setValidationErrors({});
  };

  const handleFormChange = (field: keyof DeliveryChargeFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Trigger validation based on field changes
    if (field === 'nativeDeliveryChargeName') {
      validateDeliveryChargeName(value);
    } else if (field === 'fromRange' || field === 'toRange') {
      const fromRange = field === 'fromRange' ? value : formData.fromRange;
      const toRange = field === 'toRange' ? value : formData.toRange;
      validateRanges(fromRange, toRange);
    }
  };

  const handleFetchData = async (): Promise<void> => {
    await fetchData(
      searchValue,
      pagination.page * parseInt(pageOption),
      parseInt(pageOption),
    );
  };

  useEffect(() => {
    // Initialize data with proper permission checking
    const initializeData = async () => {
      const hasViewPermission = await fetchUserPermissions();
      
      if (hasViewPermission) {
        await handleFetchData();
      }
    };

    initializeData();
  }, []);

  const handleChangePageOption = (e: { target: { value: any } }) => {
    const selectedValue = e.target.value;
    setPageOption(parseInt(selectedValue).toString());
    fetchData(
      searchValue,
      0,
      parseInt(selectedValue),
    );
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    fetchData(
      searchValue,
      (page - 1) * parseInt(pageOption),
      parseInt(pageOption),
    );
  };

  const handleSearchData = useCallback(
    debounce((searchValue: string) => {
      fetchData(
        searchValue,
        0,
        parseInt(pageOption),
      );
    }, 500),
    [pageOption]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    handleSearchData(value);
  };

  const columnsDataTableDeliveryCharge: Column[] = [
    {
      field: "srNo",
      headerName: "Sr. No",
      width: 5,
      defaultChecked: true,
      renderCell: (params: any) => (
        <Typography variant="body2" color="textSecondary">
          {pagination.page * pagination.perpage + params.row.index}
        </Typography>
      ),
    },
{
  field: "nativeDeliveryChargeName",
  headerName: "Delivery Charge Name",
  width: 150, // consider increasing width if text is long
  renderCell: (params) => (
    <Typography 
      variant="body2" 
      fontWeight={500} 
      sx={{ cursor: "default", color: "text.primary", textDecoration: "none" }}
    >
      {params.row.nativeDeliveryChargeName}
    </Typography>
  ),
},

    {
      field: "nativeDeliveryChargeValue",
      headerName: "Charge Value",
      width: 80,
      defaultChecked: true,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="500">
          {params.row.nativeDeliveryChargeValue}
        </Typography>
      ),
    },
    {
      field: "range",
      headerName: "Range",
      width: 150,
      defaultChecked: true,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.fromRange} - {params.row.toRange}
        </Typography>
      ),
    },
    {
      field: "Action",
      headerName: "Actions",
      width: 130,
      defaultChecked: true,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          {userPermissions.canEdit && (
            <IconButton 
              size="small" 
              onClick={() => handleEditDeliveryCharge(params.row)}
              sx={{ 
                color: 'warning.main',
                '&:hover': { backgroundColor: 'warning.light', color: 'white' }
              }}
            >
              <FaEdit />
            </IconButton>
          )}
          {userPermissions.canDelete && (
            <IconButton 
              size="small" 
              onClick={() => handleDeleteDeliveryCharge(params.row.nativeDeliveryChargeId)}
              disabled={deleting}
              sx={{ 
                color: 'error.main',
                '&:hover': { backgroundColor: 'error.light', color: 'white' }
              }}
            >
              {deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  const handleCloseCreateModal = () => {
    setOpenCreateModal(false);
    resetForm();
  };

  const toggleFilter = (column: Column) => {
    setSearchInputValue("");
    if (openFilter === column.field) {
      setOpenFilter(null);
    } else {
      setOpenFilter(column.field);
    }
  };

  const handleInputFocus = (field: string) => {
    setOpenFilter(field);
  };

  const handleApplyFilterBtn = () => {
    // Filter implementation if needed
  };

 

  // Show permission loading
  if (userPermissions.loading) {
    return <Loader />;
  }

  // Check view permission - UPDATED to use userPermissions
  if (!userPermissions.canView) {
    return <ErrorPage error={{ code: '403', message: 'You do not have permission to view delivery charges.' }} />;
  }

  if (tabView === '') {
    return (
      <ErrorPage
        error={{
          code: '403',
          message: "Whoops, access denied! Seems like you hit a dead end. Mysteries of the web, right?",
        }}
      />
    );
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <Helmet>
        <title>{navUrl}</title>
      </Helmet>
      <Wrapper navUrl={navUrl} customStyle={{ height: "750px" }}>
        <WrapperSubHeader className="mainBreadcrumb">
          <div className="mr-auto">
            <SubHeaderTitle subTitle={capitalizeFirstChar(tabView)} />
          </div>
        </WrapperSubHeader>
        <WrapperContent>
          <Row>
            <WrapperCard isCollapsableAnalytics={false}>
              <CustomTabPanel>
                {/* Analytics section can be added here if needed */}
              </CustomTabPanel>
              
              {/* Compact Header Section */}
              <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2} sx={{ px: 2, pt: '10px' }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    size="small"
                    placeholder="Search delivery charges..."
                    value={searchValue}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                    }}
                    sx={{ width: 250 }}
                  />
                  {/* Create button - only show if user has insert permission */}
                  {userPermissions.canInsert && (
                    <CustomButton
                      onClick={() => {
                        resetForm();
                        setOpenCreateModal(true);
                      }}
                      btnName={"Create New"}
                      variant="contained"
                      color="primary"
                      size="small"
                    />
                  )}
                </Box>
              </Box>

              {/* Compact Create/Edit Delivery Charge Modal */}
              <Dialog
                open={openCreateModal}
                onClose={handleCloseCreateModal}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                  sx: {
                    borderRadius: 2,
                    maxWidth: 500,
                  },
                }}
              >
                <DialogTitle sx={{ pb: 1 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6" fontWeight="600">
                      {formData.nativeDeliveryChargeId ? 'Edit Delivery Charge' : 'Create Delivery Charge'}
                    </Typography>
                    <IconButton onClick={handleCloseCreateModal} size="small">
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </DialogTitle>

                <DialogContent sx={{ p: 3 }}>
                  <Grid container spacing={2}>
                    {/* Delivery Charge Name */}
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                        Delivery Charge Name *
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={formData.nativeDeliveryChargeName}
                        onChange={(e) => handleFormChange('nativeDeliveryChargeName', e.target.value)}
                        placeholder="Enter delivery charge name"
                        required
                        error={showValidation && !formData.nativeDeliveryChargeName.trim() || !!validationErrors.nativeDeliveryChargeName}
                        helperText={
                          showValidation && !formData.nativeDeliveryChargeName.trim() 
                            ? "Delivery charge name is required" 
                            : validationErrors.nativeDeliveryChargeName || ""
                        }
                        disabled={validating}
                      />
                    </Grid>
                    
                    {/* Delivery Charge Value */}
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                        Delivery Charge Value *
                      </Typography>
                      <NumberInput
                        value={formData.nativeDeliveryChargeValue}
                        onChange={(value: number) => handleFormChange('nativeDeliveryChargeValue', value)}
                        placeholder="0.00"
                        required
                        error={showValidation && formData.nativeDeliveryChargeValue <= 0}
                        helperText={
                          showValidation && formData.nativeDeliveryChargeValue <= 0 
                            ? "Delivery charge value is required and must be greater than 0" 
                            : ""
                        }
                        inputProps={{ 
                          min: 0.01,
                          step: 0.01,
                          onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                              e.preventDefault();
                            }
                          }
                        }}
                      />
                    </Grid>

                    {/* Range Fields */}
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                        From Range *
                      </Typography>
                      <NumberInput
                        value={formData.fromRange}
                        onChange={(value: number) => handleFormChange('fromRange', value)}
                        placeholder="0"
                        required
                        error={showValidation && formData.fromRange < 0 || !!validationErrors.fromRange}
                        helperText={
                          showValidation ? (
                            formData.fromRange < 0 
                              ? "From range cannot be negative" 
                              : validationErrors.fromRange || ""
                          ) : validationErrors.fromRange || ""
                        }
                        inputProps={{ 
                          min: 0,
                          onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                              e.preventDefault();
                            }
                          }
                        }}
                        disabled={validating}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                        To Range *
                      </Typography>
                      <NumberInput
                        value={formData.toRange}
                        onChange={(value: number) => handleFormChange('toRange', value)}
                        placeholder="0"
                        required
                        error={showValidation && (formData.toRange < 0 || formData.toRange <= formData.fromRange) || !!validationErrors.toRange}
                        helperText={
                          showValidation ? (
                            formData.toRange < 0 
                              ? "To range cannot be negative" 
                              : formData.toRange <= formData.fromRange 
                              ? "To range must be greater than from range" 
                              : validationErrors.toRange || ""
                          ) : validationErrors.toRange || ""
                        }
                        inputProps={{ 
                          min: 0,
                          onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                              e.preventDefault();
                            }
                          }
                        }}
                        disabled={validating}
                      />
                    </Grid>

                    {/* Validation Loading Indicator */}
                    {validating && (
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={16} />
                          <Typography variant="caption" color="textSecondary">
                            Validating data...
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                    {/* Action Buttons */}
                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
                        <Button 
                          size="small" 
                          onClick={handleCloseCreateModal} 
                          disabled={saving || validating}
                          variant="outlined"
                        >
                          Cancel
                        </Button>
                        <Button 
                          size="small"
                          variant="contained" 
                          onClick={handleSaveDeliveryCharge}
                          disabled={saving || validating || !!validationErrors.nativeDeliveryChargeName || !!validationErrors.fromRange || !!validationErrors.toRange || formData.nativeDeliveryChargeValue <= 0}
                          startIcon={saving ? <CircularProgress size={16} /> : null}
                        >
                          {saving ? 'Saving...' : (formData.nativeDeliveryChargeId ? 'Update' : 'Save')}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </DialogContent>
              </Dialog>

              {/* Data Table */}
              {loadingData ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <CustomDataTable
                  rows={rows}
                  columns={columnsDataTableDeliveryCharge}
                  paginationInfo={pagination}
                  handlePageChange={handlePageChange}
                  isCheckboxShow={false}
                  FixedTable={true}
                  pageOption={pageOption}
                  handleChangePageOption={handleChangePageOption}
                  loading={loadingData}
                  isBranchFieldExist={false}
                  setCheckedValue={setCheckedValue}
                  checkedValue={checkedValue}
                  handleApplyFilterBtn={handleApplyFilterBtn}
                  toggleFilter={toggleFilter}
                  openFilter={openFilter}
                  setOpenFilter={setOpenFilter}
                  handleInputFocus={handleInputFocus}
                  branchLocationList={[]}
                  filterData={[]}
                  setFilterData={() => {}}
                  handleSearch={() => {}}
                  contactFilteredIds={[]}
                  setContactFilteredIds={() => {}}
                  filteringData={{}}
                  setSearchInputValue={setSearchInputValue}
                  searchInputValue={searchInputValue}
                  linkField={"nativeDeliveryChargeName"}
                  // linkFieldNav={linkFieldNav}
                />
              )}
            </WrapperCard>
          </Row>
        </WrapperContent>
      </Wrapper>

      {/* Snackbar for notifications - BOTTOM RIGHT */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ 
          vertical: snackbar.vertical, 
          horizontal: snackbar.horizontal 
        }}
        sx={{
          '& .MuiAlert-root': {
            fontSize: '14px',
            fontWeight: 500,
          }
        }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            width: '100%',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            '&.MuiAlert-filledSuccess': {
              backgroundColor: '#4caf50',
            },
            '&.MuiAlert-filledError': {
              backgroundColor: '#f44336',
            },
            '&.MuiAlert-filledWarning': {
              backgroundColor: '#ff9800',
            },
            '&.MuiAlert-filledInfo': {
              backgroundColor: '#2196f3',
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default DeliveryCharge;